/**
 * valueFormatters.js
 *
 * Formatter registry + business logic — mirrors valueTransforms.js's
 * role exactly. That file holds TRANSFORM_TYPES plus the pure JS
 * transform logic (applyTransform/runTransformPipeline); the Jinja
 * *rendering* of the same logic lives in bindingGenerators.js
 * (jinjaTransformStep). The Formatter follows the identical split: this
 * file owns the registry and buildFormatIntent() (business logic, zero
 * Jinja); bindingGenerators.js owns renderFormatIntent() (Jinja syntax,
 * zero business logic).
 *
 * @typedef {{ type: string, params: Object<string, *> }} FormatterConfig
 * @typedef {{ kind: string, value: string, [key: string]: * }} FormatIntent
 */

/**
 * Metadata for every supported formatter type — used by FormatterEditor.vue
 * to render the type picker and each type's parameter inputs.
 */
export const FORMATTER_TYPES = {
  none: { label: 'None', params: [] },

  // Only one representation exists today — no param needed yet. See
  // buildFormatIntent() for what this operates on.
  color: { label: 'Color', params: [] },

  percentage: { label: 'Percentage', params: [] },

  datetime: {
    label: 'Date/Time',
    params: [
      {
        key: 'style',
        label: 'Style',
        options: ['relative', 'date', 'time', 'datetime', 'duration'],
        optionLabels: {
          relative: 'Relative',
          date: 'Date',
          time: 'Time',
          datetime: 'Date & Time',
          duration: 'Duration',
        },
        default: 'relative',
      },
    ],
  },
};

/**
 * Default parameter values for a formatter type — used when its type changes.
 * @param {string} type
 * @returns {Object<string, *>}
 */
export function defaultParamsFor(type) {
  const meta = FORMATTER_TYPES[type];
  return Object.fromEntries((meta?.params ?? []).map(p => [p.key, p.default]));
}

/** @returns {FormatterConfig} the default, no-op formatter. */
export function defaultFormatter() {
  return { type: 'none', params: {} };
}

/**
 * buildFormatIntent — constructs a FormatIntent from binding.formatter: a
 * plain, domain-agnostic descriptor of what presentation is required.
 *
 * This is business logic (the same role valueTransforms.js's
 * applyTransform() plays for transforms) — it reads the binding model and
 * formatter params and decides *which* value variant a given formatter
 * kind needs (the raw pre-transform value for 'color', since it operates
 * on an array attribute the transform pipeline can't touch; the transformed
 * value for numeric/date presentation). It never constructs or interprets
 * Jinja syntax — `rawValueExpr`/`valueExpr` are opaque expression strings
 * the generator already computed and handed in; this function only decides
 * which one belongs in the intent. The selected source attribute is carried
 * as metadata for the central colour normalizer.
 *
 * FormatIntent otherwise carries generic presentation semantics
 * (`kind`, `representation`/`style`, `value`).
 *
 * Immutability is an architectural contract, not a runtime mechanism:
 * every branch returns a freshly constructed object; nothing here (or in
 * renderFormatIntent()) ever mutates an existing intent. A deliberate
 * seam for the future Expression AST — FormatIntent objects become node
 * constructors later, and this function's shape barely changes; only the
 * rendering step (bindingGenerators.js) gets replaced by a codegen visitor.
 *
 * @param {import('../ast/AutomationBinding.js').AutomationBinding} binding
 * @param {{ rawValueExpr: string, valueExpr: string }} values — both
 *   already-computed Jinja expression strings; treated opaquely here.
 * @returns {FormatIntent|null} null for the 'none' formatter — no formatting intent.
 */
export function buildFormatIntent(binding, { rawValueExpr, valueExpr }) {
  const formatter = binding.formatter ?? defaultFormatter();
  switch (formatter.type) {
    case 'color':
      return {
        kind: 'color', representation: 'rgb', value: rawValueExpr,
        attribute: binding.attribute,
      };
    case 'percentage':
      return { kind: 'percentage', value: valueExpr };
    case 'datetime':
      return { kind: 'datetime', style: formatter.params?.style ?? 'relative', value: valueExpr };
    default:
      return null;
  }
}
